import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Eye, Pencil, FileText } from 'lucide-react';
import {
  useFilterTenders,
  useDeleteTender,
  useUpdateTender,
  useDraftTendersCount,
  usePublishedTendersCount,
  useSearchTenders
} from '@/lib/api/hooks/tender';
import type { Tender } from '@/lib/api/types';
import { showToast } from '@/lib/toast';
import {
  ActionDropdown,
  TanStackTable,
  DeleteModal,
  PublishModal,
  UnpublishModal,
  type ColumnDef,
  type DropdownAction,
  Pagination,
  Button
} from '@/components/ui';

import TendersStats from './TendersStats';
import { stripHtml } from '@/utils/helpers/html';
import { Paragraph } from '@/components/ui/Typography';
import FilterParamsDisplay from '@/components/ui/FilterParamsDisplay';
import FilterModule from '@/components/ui/FilterModule';
import Search from '@/components/ui/Search';
import { useSearchFilterParams } from '@/hooks/useSearchFilterParams';
import { useTendersFilterSections } from '@/pages/opportunities/tenders/FilterModule/FILTER_SECTIONS';

type TabType = 'PUBLISH' | 'DRAFT' | 'ENDING_SOON';

const Tenders: React.FC = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<TabType>('PUBLISH');
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [publishModalOpen, setPublishModalOpen] = useState(false);
  const [unpublishModalOpen, setUnpublishModalOpen] = useState(false);
  const [selectedTenderForDelete, setSelectedTenderForDelete] = useState<Tender | null>(null);
  const [selectedTenderForStatusUpdate, setSelectedTenderForStatusUpdate] = useState<Tender | null>(null);

   const FILTER_SECTIONS = useTendersFilterSections();
                // ── URL-driven search / filter state ──────────────────────────────────────
                const {
                  mode,
                  searchTerm,
                  filterState,
                  page,
                  size,
                  setSearch,
                  setFilters,
                  removeFilter,
                  clearFilters,
                  setPage,
                  apiFilters,
                  getActiveFilterCount
                } = useSearchFilterParams(FILTER_SECTIONS)
              
              // Search API (when searching)
              const { data: searchData, isFetching: isSearchFetching, isError: isSearchError, refetch: refetchSearch } = useSearchTenders(
              searchTerm,
                mode === 'search' && activeTab !== 'ENDING_SOON'
              )
            
              // Filter API (when filtering or default)
              const { data: filterData, isFetching: isFilterFetching, isError: isFilterError, refetch: refetchFilter } = useFilterTenders(
                { page, size },
            activeTab === 'ENDING_SOON' ? { ...apiFilters } : { status: activeTab as 'DRAFT' | 'PUBLISH', ...apiFilters },
                mode === 'filter' || mode === 'default'
              )
            
              // Select appropriate data source: search takes priority
              const aggregateData = mode === 'search' ? searchData : filterData
              const isFetching = mode === 'search' ? isSearchFetching : isFilterFetching
              const isError = mode === 'search' ? isSearchError : isFilterError
            
              const totalPages = aggregateData?.data?.totalPage ?? 1
              const activeFilterCount = getActiveFilterCount()
              const refetchData = mode === 'search' ? refetchSearch : refetchFilter
              const emptyMessage = mode === 'search'
                ? 'No tenders found matching your search criteria.'
                : mode === "filter" ?  'No tenders found matching your filter criteria.'
                : 'No tenders found.'

  const { data: publishedTendersData } = useFilterTenders(
    { page: 1, size: 1000 },
    { status: 'PUBLISH' }
  );

  // const { data: filterData, isLoading: isFilterLoading, error: filterError, refetch } = useFilterTenders(
  //   { page, size: 20 },
  //   activeTab === 'ENDING_SOON' ? { ...activeFilters } : { status: activeTab as 'DRAFT' | 'PUBLISH', ...activeFilters }
  // );

  const { data: publishedCountData } = usePublishedTendersCount();
  const { data: draftCountData } = useDraftTendersCount();

  const deleteTenderMutation = useDeleteTender();
  const updateTenderMutation = useUpdateTender();

  const rawTenders = searchTerm && activeTab !== 'ENDING_SOON'
    ? (Array.isArray(searchData?.data) ? searchData.data : [])
    : (Array.isArray(filterData?.data?.data) ? filterData.data.data : []);

  const tendersByStatus = searchTerm && activeTab !== 'ENDING_SOON'
    ? rawTenders.filter((tender: Tender) => tender.status === activeTab)
    : rawTenders;

  const publishedTenders = useMemo(() => {
    return Array.isArray(publishedTendersData?.data?.data) ? publishedTendersData.data.data : [];
  }, [publishedTendersData?.data?.data]);

  const endingSoonTenders = useMemo(() => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const thirtyDaysFromNow = new Date(today);
    thirtyDaysFromNow.setDate(today.getDate() + 30);

    return publishedTenders.filter((tender: Tender) => {
      const endDate = new Date(tender.endDate);
      endDate.setHours(0, 0, 0, 0);
      return endDate >= today && endDate <= thirtyDaysFromNow;
    });
  }, [publishedTenders]);

  const publishedCount = publishedCountData?.data || 0;
  const draftCount = draftCountData?.data || 0;
  const endingSoonCount = endingSoonTenders.length;

  const filteredTenders = activeTab === 'ENDING_SOON' ? endingSoonTenders : tendersByStatus;

  const handleAddTender = () => {
    navigate('/admin/tenders/add');
  };

  const handleEditTender = (tender: Tender) => {
    navigate(`/admin/tenders/edit/${tender.tenderId}`, {
      state: { tender }
    });
  };

  const handleViewDetails = (tender: Tender) => {
    navigate(`/admin/tenders/${tender.tenderId}`);
  };

  const handleDeleteTender = (tender: Tender) => {
    setSelectedTenderForDelete(tender);
    setDeleteModalOpen(true);
  };

  const confirmDelete = () => {
    if (!selectedTenderForDelete) return;

    deleteTenderMutation.mutate(
      { tenderId: selectedTenderForDelete.tenderId },
      {
        onSuccess: (response) => {
          if (response.success) {
            showToast.success('Tender deleted successfully');
            setDeleteModalOpen(false);
            setSelectedTenderForDelete(null);
            refetchFilter();
          } else {
            showToast.error('Failed to delete tender');
          }
        },
        onError: (error: Error) => {
          showToast.error('Failed to delete: ' + (error.message || 'Unknown error'));
        }
      }
    );
  };

  const handlePublishTender = (tender: Tender) => {
    setSelectedTenderForStatusUpdate(tender);
    setPublishModalOpen(true);
  };

  const handleUnpublishTender = (tender: Tender) => {
    setSelectedTenderForStatusUpdate(tender);
    setUnpublishModalOpen(true);
  };

  const confirmStatusUpdate = () => {
    if (!selectedTenderForStatusUpdate) return;

    const newStatus = selectedTenderForStatusUpdate.status === 'PUBLISH' ? 'DRAFT' : 'PUBLISH';

    updateTenderMutation.mutate(
      {
        tenderId: selectedTenderForStatusUpdate.tenderId,
        data: {
          tenderTitle: selectedTenderForStatusUpdate.tenderTitle,
          bidType: selectedTenderForStatusUpdate.bidType,
          startDate: selectedTenderForStatusUpdate.startDate,
          endDate: selectedTenderForStatusUpdate.endDate,
          description: selectedTenderForStatusUpdate.description,
          submissionEmail: selectedTenderForStatusUpdate.submissionEmail,
          tenderUrl: selectedTenderForStatusUpdate.tenderUrl || undefined,
          status: newStatus,
          tenderCategory: selectedTenderForStatusUpdate.tenderCategory || undefined
        }
      },
      {
        onSuccess: () => {
          showToast.success(
            newStatus === 'PUBLISH'
              ? 'Tender published successfully!'
              : 'Tender unpublished successfully!'
          );
          setPublishModalOpen(false);
          setUnpublishModalOpen(false);
          setSelectedTenderForStatusUpdate(null);
          refetchFilter();
        },
        onError: (error: Error) => {
          showToast.error('Failed to update status: ' + (error.message || 'Unknown error'));
        }
      }
    );
  };

  const getDropdownActions = (tender: Tender): DropdownAction[] => {
    const actions: DropdownAction[] = [
      {
        label: tender.status === 'PUBLISH' ? 'Unpublish' : 'Publish',
        onClick: (e) => {
          e.stopPropagation();
          if (tender.status === 'PUBLISH') {
            handleUnpublishTender(tender);
          } else {
            handlePublishTender(tender);
          }
        },
        disabled: updateTenderMutation.isPending
      }
    ];

    if (tender.status === 'DRAFT') {
      actions.push({
        label: 'Delete',
        onClick: (e) => {
          e.stopPropagation();
          handleDeleteTender(tender);
        },
        variant: 'danger' as const,
        disabled: deleteTenderMutation.isPending
      });
    }

    return actions;
  };

  const formatBidType = (bidType: string) => {
    return bidType
      .split('_')
      .map(word => word.charAt(0) + word.slice(1).toLowerCase())
      .join(' ');
  };

  const formatDate = (value: string | null | undefined) => {
    if (!value) return 'N/A';
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return 'N/A';
    return date.toLocaleDateString('en-US', {
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    });
  };

  const formatDateTime = (value: string | null | undefined) => {
    if (!value) return { date: 'Not published', time: '' };
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return { date: 'Not published', time: '' };

    return {
      date: date.toLocaleDateString('en-US', { day: '2-digit', month: 'short', year: 'numeric' }),
      time: date.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' })
    };
  };

  const getInitials = (name: string) => {
    const parts = name.trim().split(' ').filter(Boolean);
    if (!parts.length) return 'UN';
    if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
    return `${parts[0][0]}${parts[parts.length - 1][0]}`.toUpperCase();
  };

  const getTenderRef = (tender: Tender) => {
    const year = tender.datePublished ? new Date(tender.datePublished).getFullYear() : new Date().getFullYear();
    const suffix = tender.tenderId.slice(-3).toUpperCase();
    return `AFSA/PROC/${year}/${suffix}`;
  };

  const getTenderStatus = (tender: Tender) => {
    if (tender.status === 'DRAFT') {
      return {
        label: 'Draft',
        description: 'Not published',
        className: 'bg-blue-50 text-blue-700 border-blue-200'
      };
    }

    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const endDate = new Date(tender.endDate);
    endDate.setHours(0, 0, 0, 0);
    const daysLeft = Math.ceil((endDate.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));

    if (tender.isExpired || daysLeft < 0) {
      return {
        label: 'Closed',
        description: 'Not accepting bids',
        className: 'bg-gray-100 text-gray-700 border-gray-200'
      };
    }

    if (daysLeft <= 1) {
      return {
        label: 'Closing Soon',
        description: 'Live on website',
        className: 'bg-amber-50 text-amber-700 border-amber-200'
      };
    }

    return {
      label: 'Published',
      description: 'Live on website',
      className: 'bg-emerald-50 text-emerald-700 border-emerald-200'
    };
  };

  const getDeadlineMeta = (tender: Tender) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const endDate = new Date(tender.endDate);
    endDate.setHours(0, 0, 0, 0);

    const daysLeft = Math.ceil((endDate.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));

    let subtitle = 'Closed';
    let subtitleClass = 'text-gray-500';
    if (daysLeft < 0 || tender.isExpired) {
      subtitle = 'Closed';
      subtitleClass = 'text-gray-500';
    } else if (daysLeft === 0) {
      subtitle = 'Today';
      subtitleClass = 'text-amber-600';
    } else if (daysLeft === 1) {
      subtitle = 'Tomorrow';
      subtitleClass = 'text-amber-600';
    } else {
      subtitle = `${daysLeft} days left`;
      subtitleClass = daysLeft <= 7 ? 'text-red-600' : 'text-gray-500';
    }

    return {
      date: formatDate(tender.endDate),
      subtitle,
      dateClass: daysLeft <= 7 && daysLeft >= 0 ? 'text-red-600' : 'text-gray-900',
      subtitleClass
    };
  };

  const columns: ColumnDef<Tender, unknown>[] = [
    {
      id: 'tenderTitle',
      header: 'TENDER TITLE',
      cell: ({ row }) => {
        const tender = row.original;
        return (
          <div className="grid grid-cols-[auto_1fr] items-start gap-3 min-w-[400px]">
            <div className="h-full w-16 rounded-xl bg-[#F4F1FF] text-[#7C3AED] flex items-center justify-center">
              {
                tender.tenderUrl                ? <img src={tender.tenderUrl} alt={tender.tenderTitle} className="h-full w-full object-cover rounded" />
                : <FileText className="h-6 w-6" />
              }
            </div>
            <div className="overflow-hidden flex flex-col gap-1">
              <p className="text-base font-semibold text-gray-900 leading-tight">{tender.tenderTitle}</p>
              <p className="text-xs text-gray-500">REF: {getTenderRef(tender)}</p>
              <p className="text-sm text-gray-600 line-clamp-1 text-ellipsis">{tender.description ? stripHtml(tender.description) : 'No description provided'}</p>
            </div>
          </div>
        );
      },
      meta: { headerClassName: 'px-4 py-4 !normal-case text-gray-600', cellClassName: 'px-4 py-4' },
    },
    {
      id: 'type',
      header: 'TYPE',
      cell: ({ row }) => {
        const tender = row.original;
        return (
          <div className="min-w-[180px]">
            <p className="text-sm font-semibold text-gray-900">{formatBidType(tender.bidType)}</p>
          </div>
        );
      },
      meta: { headerClassName: 'px-4 py-4 !normal-case text-gray-600', cellClassName: 'px-4 py-4' },
    },
    {
      id: 'status',
      header: 'STATUS',
      cell: ({ row }) => {
        const status = getTenderStatus(row.original);
        return (
          <div className="min-w-[160px]">
            <span className={`inline-flex items-center rounded-md border px-2 py-1 text-sm font-semibold ${status.className}`}>
              {status.label}
            </span>
            <p className="text-sm text-gray-500 mt-1">{status.description}</p>
          </div>
        );
      },
      meta: { headerClassName: 'px-4 py-4 !normal-case text-gray-600', cellClassName: 'px-4 py-4' },
    },
    {
      id: 'publishedOn',
      header: 'PUBLISHED ON',
      cell: ({ row }) => {
        const { date, time } = formatDateTime(row.original.datePublished);
        return (
          <div className="min-w-[140px]">
            <p className="text-sm text-gray-900">{date}</p>
            {time && <p className="text-sm text-gray-500 mt-1">{time}</p>}
          </div>
        );
      },
      meta: { headerClassName: 'px-4 py-4 !normal-case text-gray-600', cellClassName: 'px-4 py-4 whitespace-nowrap' },
    },
    {
      id: 'deadline',
      header: 'DEADLINE',
      cell: ({ row }) => {
        const deadline = getDeadlineMeta(row.original);
        return (
          <div className="min-w-[140px]">
            <p className={`text-sm font-semibold ${deadline.dateClass}`}>{deadline.date}</p>
            <p className={`text-sm mt-1 ${deadline.subtitleClass}`}>{deadline.subtitle}</p>
          </div>
        );
      },
      meta: { headerClassName: 'px-4 py-4 !normal-case text-gray-600', cellClassName: 'px-4 py-4 whitespace-nowrap' },
    },
    {
      id: 'owner',
      header: 'OWNER',
      cell: ({ row }) => {
        const tender = row.original;
        const ownerName = tender.publishedByName && tender.publishedByName !== 'N/A'
          ? tender.publishedByName
          : tender.createdByName || 'Unknown';

        return (
          <div className="flex items-center gap-3 min-w-[220px]">
            <div className="h-10 w-10 rounded-full bg-emerald-50 text-emerald-700 text-sm font-semibold flex items-center justify-center shrink-0">
              {getInitials(ownerName)}
            </div>
            <div className="min-w-0">
              <p className="text-sm text-gray-900 truncate">{ownerName}</p>
      
            </div>
          </div>
        );
      },
      meta: { headerClassName: 'px-4 py-4 !normal-case text-gray-600', cellClassName: 'px-4 py-4' },
    },
    {
      id: 'actions',
      header: 'ACTIONS',
      cell: ({ row }) => {
        const tender = row.original;
        return (
          <div className="flex items-center gap-2" onClick={(e) => e.stopPropagation()}>
            <button
              type="button"
              title="View"
              onClick={() => handleViewDetails(tender)}
              className="h-11 w-11 inline-flex items-center justify-center rounded-xl border border-gray-200 text-emerald-700 hover:bg-emerald-50"
            >
              <Eye className="w-5 h-5" />
            </button>
            <button
              type="button"
              title="Edit"
              onClick={() => handleEditTender(tender)}
              className="h-11 w-11 inline-flex items-center justify-center rounded-xl border border-gray-200 text-emerald-700 hover:bg-emerald-50"
            >
              <Pencil className="w-5 h-5" />
            </button>
            <div className="h-11 w-11 inline-flex items-center justify-center rounded-xl border border-gray-200 text-gray-600 hover:bg-gray-50">
              <ActionDropdown id={tender.tenderId} actions={getDropdownActions(tender)} />
            </div>
          </div>
        );
      },
      meta: { headerClassName: 'px-4 py-4 !normal-case text-gray-600', cellClassName: 'px-4 py-4 whitespace-nowrap' },
    },
  ];

  const isEmptyState = !isFetching && mode === "default" && filteredTenders.length === 0;

  return (
    <div className="flex flex-col gap-6">
      <TendersStats />

      <div className="flex items-center justify-between">
        <div className="flex items-center gap-0">
          <button
            onClick={() => {
              setActiveTab('PUBLISH');
              setPage(1);
            }}
            className={`px-6 py-2.5 text-sm font-semibold transition-colors rounded-md ${activeTab === 'PUBLISH'
              ? 'bg-wine-100 text-white'
              : 'text-gray-700 hover:bg-gray-100'
              }`}
          >
            Published ({publishedCount})
          </button>
          <button
            onClick={() => {
              setActiveTab('DRAFT');
              setPage(1);
            }}
            className={`px-6 py-2.5 text-sm font-semibold transition-colors rounded-md ${activeTab === 'DRAFT'
              ? 'bg-wine-100 text-white'
              : 'text-gray-700 hover:bg-gray-100'
              }`}
          >
            Drafts ({draftCount})
          </button>
          <button
            onClick={() => {
              setActiveTab('ENDING_SOON');
              setPage(1);
            }}
            className={`px-6 py-2.5 text-sm font-semibold transition-colors ${activeTab === 'ENDING_SOON'
              ? 'bg-wine-100 text-white'
              : 'text-gray-700 hover:bg-gray-100'
              }`}
          >
            Ending soon ({endingSoonCount})
          </button>
        </div>
        <button
          onClick={handleAddTender}
          className="bg-[#047857] hover:bg-[#059669] text-white font-semibold py-2.5 px-6 rounded-md text-sm"
        >
          Post Tenders
        </button>
      </div>

      <div className="rounded-xl border border-gray-200 bg-white shadow-sm overflow-hidden">
         {/* Search + Filter Controls */}
                          <div className="flex flex-col gap-4 p-4 border border-gray-200 rounded-md bg-white shadow-sm">
                            <div className="flex items-center relative gap-4 justify-between flex-wrap">
                              <Search
                                value={searchTerm}
                                onSearch={setSearch}
                                placeholder="Search"
                              />
                               <FilterModule
                                    sections={FILTER_SECTIONS}
                                    activeFilters={filterState}
                                    onApply={setFilters}
                                    activeFilterCount={activeFilterCount}
                                  />
                            </div>
                
                            {/* Active filter chips — only shown in filter mode */}
                            <FilterParamsDisplay
                              sections={FILTER_SECTIONS}
                              filterState={filterState}
                              onRemove={removeFilter}
                              onClearAll={clearFilters}
                            />
                          </div>

        {isFetching ? <TanStackTable data={[]} columns={columns} loading /> :
              isError ? (
          <div className="p-8 flex items-center justify-center  flex-col gap-4 text-center">
            <Paragraph className="">Error loading data.</Paragraph>
            <Button
              onClick={() => {
                refetchData();
              }}
              className=""
            >
              Retry
            </Button>
          </div>
        ) : isEmptyState ? (
                <div className="text-center py-20">
                  <div className="inline-block bg-gray-100 p-4 rounded-full mb-4">
                    <img src="/No-post-icon.png" alt="No posts" className="w-10 h-10 object-contain" />
                  </div>
                  <h3 className="font-semibold text-gray-800 text-lg">
                    {activeTab === 'PUBLISH' ? 'No Recent Tenders' : 'No Draft'}
                  </h3>
                  <p className="mt-1 text-sm text-gray-500">
                    {activeTab === 'PUBLISH' ? 'You are yet to create a tender' : 'Your draft is empty'}
                  </p>
                    <Button variant="primary" className="mt-4"       onClick={handleAddTender}>
                     Post Tender
                    </Button>
                </div>
              ) : (
          <>
          <div className="overflow-x-auto">
            <TanStackTable<Tender>
              data={filteredTenders}
              columns={columns}
              getRowId={(row) => row.tenderId}
              onRowClick={(row) => handleViewDetails(row)}
              tableClassName="min-w-[1450px] w-full divide-y divide-gray-200"
              theadClassName="bg-[#FAFAFA]"
              tbodyClassName="bg-white divide-y divide-gray-200"
              rowClassName="hover:bg-gray-50 cursor-pointer transition-colors"
              emptyMessage={emptyMessage}
              emptyClassName="px-6 py-12 text-center text-sm text-gray-500"
            />
          </div>
                    <Pagination
                                   currentPage={page}
                        totalPages={totalPages}
                        onPageChange={setPage}
                                className="border-t border-gray-200 p-4"
                              />
          </>
        )}
      </div>

      <DeleteModal
        isOpen={deleteModalOpen}
        onClose={() => setDeleteModalOpen(false)}
        onConfirm={confirmDelete}
        title="Delete Tender"
        subtitle="Are you sure you want to delete this tender?"
        itemName={selectedTenderForDelete?.tenderTitle}
        warningText="This action cannot be undone. The tender will be permanently deleted."
        isLoading={deleteTenderMutation.isPending}
      />

      {selectedTenderForStatusUpdate && selectedTenderForStatusUpdate.status === 'DRAFT' && (
        <PublishModal
          isOpen={publishModalOpen}
          onClose={() => setPublishModalOpen(false)}
          onConfirm={confirmStatusUpdate}
          title="Publish Tender?"
          subtitle="Are you sure you want to publish this tender?"
          itemName={selectedTenderForStatusUpdate.tenderTitle}
          infoText="This tender will be visible to the public once published."
          itemType="Tender"
          isLoading={updateTenderMutation.isPending}
        />
      )}

      {selectedTenderForStatusUpdate && selectedTenderForStatusUpdate.status === 'PUBLISH' && (
        <UnpublishModal
          isOpen={unpublishModalOpen}
          onClose={() => setUnpublishModalOpen(false)}
          onConfirm={confirmStatusUpdate}
          itemType="Tender"
          isLoading={updateTenderMutation.isPending}
        />
      )}
    </div>
  );
};

export default Tenders;
